import { supabase } from '@/integrations/supabase/client';
import { DealStatusService } from './dealStatusService';

export class ProposalAcceptanceService {
  static async fetchDealWithContact(dealId: string) {
    console.log('🔍 [ProposalAcceptanceService] Fetching deal with contact:', dealId);
    
    const { data: deal, error } = await supabase
      .from('crm_deals')
      .select(`
        *,
        contact:profiles!crm_deals_contact_id_fkey(
          id,
          first_name,
          last_name,
          email
        )
      `)
      .eq('id', dealId)
      .single();

    if (error) {
      console.error('❌ [ProposalAcceptanceService] Error fetching deal:', error);
      throw new Error(`Failed to fetch deal: ${error.message}`);
    }

    console.log('✅ [ProposalAcceptanceService] Deal fetched successfully:', deal);
    return deal;
  }

  /**
   * MAIN METHOD: Accept proposal using secure database function
   * This is the new primary method that bypasses RLS issues
   */
  static async acceptProposal(
    notificationId: string, 
    dealId: string, 
    deliveryAddressId?: string | null,
    orderReference?: string | null,
    updatedProposalData?: any
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚀 [ProposalAcceptanceService] Starting secure proposal acceptance');
      console.log('📋 [ProposalAcceptanceService] Notification:', notificationId, 'Deal:', dealId, 'Delivery Address:', deliveryAddressId, 'Order Reference:', orderReference);

      // Step 1: SAFE EDIT MODEL — store any customer edits to `proposal_edits`
      // via the edge function. The original `crm_deals` row is NOT overwritten
      // here; it is only updated below, at acceptance time.
      if (updatedProposalData) {
        const dealWithContact = await this.fetchDealWithContact(dealId).catch(() => null);
        const contactEmail = (dealWithContact as any)?.contact?.email;
        if (contactEmail) {
          const { error: editErr } = await supabase.functions.invoke('update-customer-proposal', {
            body: {
              dealId,
              contactEmail,
              updatedData: updatedProposalData,
              originalDealData: dealWithContact,
            },
          });
          if (editErr) {
            console.warn('⚠️ [ProposalAcceptanceService] Failed to record customer edit:', editErr);
          }
        } else {
          console.warn('⚠️ [ProposalAcceptanceService] No contact email on deal; skipping edit record');
        }
      }

      // Step 1b: Apply the latest edit (if any) to the deal immediately before
      // moving to won. This mirrors the proposal-accept edge function path.
      const applyResult = await this.applyLatestEditToDeal(dealId);
      if (!applyResult) {
        console.warn('⚠️ [ProposalAcceptanceService] Could not apply latest edit (continuing)');
      }

      // Step 2: Mark deal as won using secure database function
      const dealResult = await DealStatusService.markDealAsWon(dealId);
      if (!dealResult.success) {
        return { success: false, error: dealResult.error || 'Failed to mark deal as won' };
      }

      // Step 3: Update notification status
      const notificationResult = await this.updateNotificationStatus(notificationId);
      if (!notificationResult) {
        console.warn('⚠️ [ProposalAcceptanceService] Deal updated but notification update failed');
        // Don't fail entire operation for notification update failure
      }

      // Step 4: If delivery address, order reference, or updated data is provided, update the order
      if (deliveryAddressId || orderReference || updatedProposalData) {
        const orderUpdateResult = await this.updateOrderDeliveryAddress(dealId, deliveryAddressId, orderReference, updatedProposalData);
        if (!orderUpdateResult) {
          console.warn('⚠️ [ProposalAcceptanceService] Could not update order delivery address/reference/data');
          // Don't fail entire operation for this
        }
      }

      // Step 5: Verify the deal is properly won
      const verification = await DealStatusService.verifyWonDealStatus(dealId);
      if (!verification.isProperlyWon) {
        return { success: false, error: 'Deal update verification failed - deal not properly marked as won' };
      }

      console.log('🎉 [ProposalAcceptanceService] Proposal acceptance completed successfully!');
      return { success: true };

    } catch (error: any) {
      console.error('💥 [ProposalAcceptanceService] Unexpected error:', error);
      return { success: false, error: `Unexpected error: ${error.message}` };
    }
  }

  /**
   * Fallback method for direct database updates (legacy)
   * This method may fail due to RLS, so we use the secure function instead
   */
  static async updateDealToWon(dealId: string): Promise<boolean> {
    try {
      console.log('🔄 [ProposalAcceptanceService] Attempting direct database update (fallback)');
      
      const { error } = await supabase
        .from('crm_deals')
        .update({ 
          status: 'won',
          stage_id: null, // CRITICAL: Won deals should have no stage
          probability: 100,
          updated_at: new Date().toISOString(),
          proposal_accepted_at: new Date().toISOString()
        })
        .eq('id', dealId);

      if (error) {
        console.error('❌ [ProposalAcceptanceService] Direct update failed (expected due to RLS):', error);
        return false;
      }

      console.log('✅ [ProposalAcceptanceService] Direct update succeeded');
      return true;
    } catch (error) {
      console.error('💥 [ProposalAcceptanceService] Direct update exception:', error);
      return false;
    }
  }

  static async updateNotificationStatus(notificationId: string): Promise<boolean> {
    try {
      console.log('🔄 [ProposalAcceptanceService] Updating notification status');
      
      const { error } = await supabase
        .from('proposal_notifications')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) {
        console.error('❌ [ProposalAcceptanceService] Notification update failed:', error);
        return false;
      }

      console.log('✅ [ProposalAcceptanceService] Notification status updated');
      return true;
    } catch (error) {
      console.error('💥 [ProposalAcceptanceService] Exception during notification update:', error);
      return false;
    }
  }

  // Legacy methods kept for backward compatibility
  static async verifyDealStatus(dealId: string): Promise<{ success: boolean; dealInfo?: any }> {
    const result = await DealStatusService.verifyWonDealStatus(dealId);
    return { success: result.isProperlyWon, dealInfo: result.dealInfo };
  }

  static async tryEdgeFunction(dealId: string, contactEmail: string): Promise<boolean> {
    try {
      console.log('🚀 [ProposalAcceptanceService] Attempting edge function approach (legacy)');
      
      const { data, error } = await supabase.functions.invoke('proposal-accept', {
        body: { dealId, contactEmail }
      });

      if (error) {
        console.error('❌ [ProposalAcceptanceService] Edge function error:', error);
        return false;
      }

      console.log('✅ [ProposalAcceptanceService] Edge function success:', data);
      return true;
    } catch (error) {
      console.error('💥 [ProposalAcceptanceService] Edge function exception:', error);
      return false;
    }
  }

  static async updateDealStatusDirect(dealId: string): Promise<boolean> {
    // This is now just an alias to the fallback method
    return this.updateDealToWon(dealId);
  }

  static async updateOrderDeliveryAddress(dealId: string, deliveryAddressId?: string | null, orderReference?: string | null, updatedProposalData?: any): Promise<boolean> {
    try {
      console.log('🚚 [ProposalAcceptanceService] Updating order delivery address and reference');
      
      // Find the order associated with this deal
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .select('id')
        .eq('deal_id', dealId)
        .single();

      if (saleError || !sale) {
        console.error('❌ [ProposalAcceptanceService] Could not find sale for deal:', saleError);
        return false;
      }

      // Prepare update data
      const updateData: any = {};
      if (deliveryAddressId) {
        updateData.delivery_address_id = deliveryAddressId;
      }
      if (orderReference) {
        updateData.order_reference = orderReference;
      }
      
      // Add updated proposal data to order
      if (updatedProposalData?.order_data) {
        updateData.order_data = updatedProposalData.order_data;
        updateData.total_cameras = updatedProposalData.total_cameras || 0;
        updateData.total_images = updatedProposalData.total_images || 0;
      }

      // Update the order with the delivery address and/or order reference
      const { error: orderError } = await supabase
        .from('orders')
        .update(updateData)
        .eq('sale_id', sale.id);

      if (orderError) {
        console.error('❌ [ProposalAcceptanceService] Failed to update order:', orderError);
        return false;
      }

      // Also update the sales record with order reference if provided
      if (orderReference) {
        const { error: salesError } = await supabase
          .from('sales')
          .update({ order_reference: orderReference })
          .eq('id', sale.id);

        if (salesError) {
          console.warn('⚠️ [ProposalAcceptanceService] Failed to update sales order reference:', salesError);
          // Don't fail the entire operation for this
        }
      }

      // Update the crm_deals table with order reference if provided
      if (orderReference) {
        const { error: dealError } = await supabase
          .from('crm_deals')
          .update({ order_reference: orderReference })
          .eq('id', dealId);

        if (dealError) {
          console.warn('⚠️ [ProposalAcceptanceService] Failed to update deal order reference:', dealError);
          // Don't fail the entire operation for this
        }
      }

      console.log('✅ [ProposalAcceptanceService] Order updated successfully');
      return true;
    } catch (error) {
      console.error('💥 [ProposalAcceptanceService] Exception updating order:', error);
      return false;
    }
  }

  static async updateDealData(dealId: string, updatedProposalData: any): Promise<boolean> {
    // RETAINED for backwards compatibility, but the safe acceptance path no
    // longer calls this method. Customer edits are recorded in `proposal_edits`
    // and applied via `applyLatestEditToDeal` at acceptance time.
    try {
      console.log('🔄 [ProposalAcceptanceService] Updating deal data with proposal changes');
      
      const updateData: any = {};
      
      if (updatedProposalData.title) {
        updateData.title = updatedProposalData.title;
      }
      if (updatedProposalData.value !== undefined) {
        updateData.value = updatedProposalData.value;
      }
      if (updatedProposalData.total_cameras !== undefined) {
        updateData.total_cameras = updatedProposalData.total_cameras;
      }
      if (updatedProposalData.total_images !== undefined) {
        updateData.total_images = updatedProposalData.total_images;
      }
      if (updatedProposalData.total_revenue !== undefined) {
        updateData.total_revenue = updatedProposalData.total_revenue;
      }
      if (updatedProposalData.image_revenue !== undefined) {
        updateData.image_revenue = updatedProposalData.image_revenue;
      }
      if (updatedProposalData.deal_data) {
        const { data: existingDeal } = await supabase
          .from('crm_deals')
          .select('deal_data')
          .eq('id', dealId)
          .single();

        updateData.deal_data = {
          ...((existingDeal?.deal_data as Record<string, any>) || {}),
          ...updatedProposalData.deal_data,
        };
      }

      const { error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', dealId);

      if (error) {
        console.error('❌ [ProposalAcceptanceService] Failed to update deal data:', error);
        return false;
      }

      console.log('✅ [ProposalAcceptanceService] Deal data updated successfully');
      return true;
    } catch (error) {
      console.error('💥 [ProposalAcceptanceService] Exception updating deal data:', error);
      return false;
    }
  }

  /**
   * Apply the latest stored proposal_edit (if any) to crm_deals immediately
   * before marking the deal as won. This is the safe replacement for the
   * previous "overwrite before accept" path — the staff-authored opportunity
   * stays intact unless and until the customer actually accepts.
   */
  static async applyLatestEditToDeal(dealId: string): Promise<boolean> {
    try {
      const { data: notif } = await supabase
        .from('proposal_notifications')
        .select('id')
        .eq('deal_id', dealId)
        .maybeSingle();
      if (!notif?.id) return true;

      const { data: latestEdit } = await supabase
        .from('proposal_edits')
        .select('edited_proposal_data, version')
        .eq('proposal_notification_id', notif.id)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();

      const edit: any = (latestEdit as any)?.edited_proposal_data;
      if (!edit) return true;

      const { data: existingDeal } = await supabase
        .from('crm_deals')
        .select('deal_data')
        .eq('id', dealId)
        .single();

      const updatePayload: Record<string, any> = {
        accepted_proposal_data: edit,
        updated_at: new Date().toISOString(),
      };
      if (edit.title) updatePayload.title = edit.title;
      if (edit.value !== undefined) updatePayload.value = edit.value;
      if (edit.total_cameras !== undefined) updatePayload.total_cameras = edit.total_cameras;
      if (edit.total_images !== undefined) updatePayload.total_images = edit.total_images;
      if (edit.total_revenue !== undefined) updatePayload.total_revenue = edit.total_revenue;
      if (edit.image_revenue !== undefined) updatePayload.image_revenue = edit.image_revenue;
      if (edit.deal_data) {
        updatePayload.deal_data = {
          ...(((existingDeal as any)?.deal_data as Record<string, any>) || {}),
          ...edit.deal_data,
        };
      }

      const { error: applyErr } = await supabase
        .from('crm_deals')
        .update(updatePayload)
        .eq('id', dealId);
      if (applyErr) {
        console.error('❌ [ProposalAcceptanceService] applyLatestEditToDeal failed:', applyErr);
        return false;
      }
      console.log(`✅ [ProposalAcceptanceService] Applied customer edit v${(latestEdit as any)?.version} to deal ${dealId}`);
      return true;
    } catch (e) {
      console.error('💥 [ProposalAcceptanceService] applyLatestEditToDeal exception:', e);
      return false;
    }
  }
}
